import {
    AxiosCartError,
    Cart,
    CartError,
    CartErrorTypes,
    CartSession,
    CheckoutPaymentMethods,
    CheckoutRejectionErrorType,
    FailureReason,
    ImageFormat,
    ImageType,
    PreparedVariation,
    ProductImageData,
    StockLevelStatuses,
    VariantMatrix,
} from '@telialt/models/src/EshopModel';

import {
    CART_SESSION_KEY,
    CHECKOUT_ORDER_PLACED_KEY,
    CHECKOUT_PAYMENT_SUBMITTED_KEY,
    OUT_OF_STOCK_ERROR_KEY,
    PARAM_GUID_QUERY,
    PARAM_ID_QUERY,
    PREVIOUS_PRODUCT_KEY,
    PREVIOUS_PRODUCT_URL_KEY,
} from './eshopConstants';
import {
    selectHasDevice,
    selectHasRatePlan,
    selectIsCartAnonymous,
    selectIsPreviouslyCloned,
    selectIsWithEsim,
    selectPopPaymentStatus,
    selectProductInCart,
    selectProductInCartCategory,
} from './eshopSelectors';
import { getHybrisSegment, isDeepEqual, withParams } from './helpers';
import QueryString from './QueryString';
import { isMobileRes, isResMoreThanExtraSmall } from './responsive';
import { getIsUserAuthorized } from './SSO/utils';

export const getProductImageData = (
    images: ProductImageData[],
    type: `${ImageType}`,
    format: `${ImageFormat}`,
): ProductImageData | undefined => {
    return images?.find((image) => image.imageType === type && image.format === format);
};

export const removePriceMonthLabel = (price: string = '', delimiter: string = '/') => price.split(delimiter)[0];

export const appendCartGuidQuery = (nextCartActionUrl: string, cart: Cart) =>
    `${nextCartActionUrl}?${new URLSearchParams({ cartGuid: cart?.guid })}`;

export const setCartSession = (cartSessionVars: CartSession) => {
    sessionStorage.setItem(CART_SESSION_KEY, JSON.stringify(cartSessionVars));
};
export const clearCartSession = () => sessionStorage.removeItem(CART_SESSION_KEY);
export const getCartSession = (): CartSession | null => {
    const cartSessionKey = sessionStorage.getItem(CART_SESSION_KEY);

    return cartSessionKey ? JSON.parse(cartSessionKey) : null;
};

export const getCartIdSync = <T extends { code: string; guid: string }>(cart: T) =>
    getIsUserAuthorized() ? cart.code : cart.guid;

export const getCheckoutRejectionErrorType = (cart: Cart): CheckoutRejectionErrorType => {
    const cartId = getCartIdSync(cart);
    if (window.sessionStorage.getItem(OUT_OF_STOCK_ERROR_KEY) === cartId) {
        return CheckoutRejectionErrorType.OutOfStock;
    }
    if (cart.failureReason === FailureReason.Payment || selectPopPaymentStatus(cart)) {
        return CheckoutRejectionErrorType.Payment;
    }

    // ToDo: handle "PERSONAL_CODE" rejection reason
    return CheckoutRejectionErrorType.General;
};

const createStartOverLink = (cart: Cart) => {
    const type = getCheckoutRejectionErrorType(cart);

    return type === CheckoutRejectionErrorType.OutOfStock
        ? selectProductInCartCategory(cart)?.url
        : selectProductInCart(cart)?.url;
};

export const startOver = () => {
    const previousProductUrl = getStartOverLink();
    window.history.pushState(null, 'startOver', previousProductUrl); // solves issue LTDAT-11973, when location.assign setter is overriden by "this.end()" method from sso.session.ts
    window.location.assign(previousProductUrl);
};

const getOutOfStock = (variantMatrix: VariantMatrix) =>
    variantMatrix.isLeaf
        ? variantMatrix.variantOption.stock.stockLevelStatus === StockLevelStatuses.NoStock
        : variantMatrix.elements?.every(getOutOfStock) || false;

export const toPreparedVariations = (variantMatrixList: VariantMatrix[] = []) => {
    const prepare = (
        el: VariantMatrix,
        depthLvl: number,
        parentsKey?: string,
        pathFromRoot: string[] = [],
    ): PreparedVariation => ({
        key: el.variantValueCategory.name,
        code: el.variantOption.code,
        alias: el.parentVariantCategory.alias,
        isOutOfStock: getOutOfStock(el),
        data: { ...el.variantValueCategory, ...el.variantOption },
        parentsKey,
        pathFromRoot: [...pathFromRoot, el.variantValueCategory.name],
        depthLvl,
        isLeaf: el.isLeaf,
        ...(!el.isLeaf && {
            elements: el.elements?.map((child) =>
                prepare(child, depthLvl + 1, el.variantValueCategory.name, [
                    ...pathFromRoot,
                    el.variantValueCategory.name,
                ]),
            ),
        }),
    });

    return variantMatrixList.map<PreparedVariation>((el): PreparedVariation => prepare(el, 1));
};

type WithElements = Record<any, any> & { elements?: WithElements[] };

export function depthFirstSearch<T extends WithElements>(
    nodes: T[] = [],
    condition: (node: T) => boolean,
): T | undefined {
    const queue = [...nodes];
    let result: T | undefined;

    while (queue.length && !result) {
        const node = queue.shift() as T | undefined;
        if (node?.elements?.length) {
            queue.unshift(...node.elements);
        }
        if (condition(node as T)) {
            result = node;
        }
    }

    return result;
}

export const getFirstLeaf = <T extends WithElements>(nodes: T[] = []): T | undefined => {
    const c = [...nodes].shift();
    if (c?.elements?.length) {
        return getFirstLeaf(c.elements);
    }

    return c;
};
export const getByKey = (
    preparedVariations: PreparedVariation[] = [],
    key: string | undefined,
): PreparedVariation | undefined => {
    return preparedVariations.find((c) => c.key === key);
};
export const findLeafByCode = (nodes: PreparedVariation[] = [], code: string | undefined) => {
    if (!code) {
        return undefined;
    }
    const byLeafAndCode = (node: PreparedVariation) => node.code === code && node.isLeaf;

    return depthFirstSearch(nodes, byLeafAndCode);
};

export const findByPathFromRoot = (nodes: PreparedVariation[] = [], pathFromRootToSearch: string[]) => {
    const byPathFromRoot = (node: PreparedVariation) =>
        JSON.stringify(node.pathFromRoot) === JSON.stringify(pathFromRootToSearch);

    return depthFirstSearch(nodes, byPathFromRoot);
};

export const byIsInStock = (v: PreparedVariation) => !v.isOutOfStock;

export const getFirstInStockLeaf = (preparedVariations: PreparedVariation[] = []): PreparedVariation | undefined => {
    return depthFirstSearch(preparedVariations, (node) => !!node && byIsInStock(node) && node.isLeaf);
};

export const extractFirstLevelKey = (preparedVariation: PreparedVariation): string | undefined => {
    if (preparedVariation.depthLvl === 1) {
        return preparedVariation.key;
    }
    if (preparedVariation.depthLvl === 2) {
        return preparedVariation.parentsKey;
    }

    return undefined;
};

export const isCartLoadedFromUrl = (search: string) => {
    const cartGuid = new QueryString(search).getParam(PARAM_GUID_QUERY);
    const cartCode = new QueryString(search).getParam(PARAM_ID_QUERY);
    const isDifferentCartGuid = getCartSession()?.guid !== cartGuid;
    const isDifferentCartCode = getCartSession()?.code !== cartCode;

    return (cartGuid && isDifferentCartGuid) || (cartCode && isDifferentCartCode);
};

export const updateCartSession = (cart: Cart): CartSession | undefined => {
    if (cart?.code && cart?.guid) {
        const cartSession = getCartSession();
        const newCartSession: CartSession = {
            code: cart.code,
            guid: cart.guid,
            isCartAnonymous: selectIsCartAnonymous(cart),
            isPreviouslyCloned: selectIsPreviouslyCloned(cart),
        };

        if (!isDeepEqual(cartSession, newCartSession)) {
            setCartSession(newCartSession);
        }

        return newCartSession;
    }

    return undefined;
};

export const setPreviousProduct = (productCode: string) => sessionStorage.setItem(PREVIOUS_PRODUCT_KEY, productCode);
export const getPreviousProduct = () => sessionStorage.getItem(PREVIOUS_PRODUCT_KEY);

export const setStartOverLink = (url: string) => sessionStorage.setItem(PREVIOUS_PRODUCT_URL_KEY, url);
export const getStartOverLink = () => sessionStorage.getItem(PREVIOUS_PRODUCT_URL_KEY) || '/';
export const updateStartOverLink = (cart: Cart) => setStartOverLink(createStartOverLink(cart) || getStartOverLink());

export const setOrderPlacedId = (cartId: string) => {
    sessionStorage.setItem(CHECKOUT_ORDER_PLACED_KEY, cartId);
    dispatchEvent(new Event('storage'));
};
export const getOrderPlacedId = () => sessionStorage.getItem(CHECKOUT_ORDER_PLACED_KEY);

export const setPaymentSubmittedId = (cartId: string) => sessionStorage.setItem(CHECKOUT_PAYMENT_SUBMITTED_KEY, cartId);
export const getPaymentSubmittedId = () => sessionStorage.getItem(CHECKOUT_PAYMENT_SUBMITTED_KEY);

export const toCartError = (axiosError: AxiosCartError): CartError => {
    const response = axiosError?.response;
    const error = response?.data?.errors?.[0];
    const newCartError: CartError = {
        type: error?.type || CartErrorTypes.CartError,
        reason: error?.reason,
        message: error?.message,
        status: response?.status,
        data: error || response?.data,
        time: new Date(),
    };

    return axiosError?.isAxiosError ? newCartError : { type: CartErrorTypes.ConnectionError, time: new Date() };
};

export const getMinutesAndSmsString = (includedMinuteCount: number, includedSmsCount: number) => {
    const infinite = 'Neribotos';
    const sms = includedSmsCount === -1 ? infinite.toLowerCase() : includedSmsCount;
    const minutes = includedMinuteCount === -1 ? infinite : includedMinuteCount;

    if (includedMinuteCount === -1 && includedSmsCount === -1) {
        return `${infinite} MIN ir SMS`;
    }

    if (!minutes && sms) {
        if (includedSmsCount === -1) {
            return `${infinite} SMS`;
        }

        return `${sms} SMS`;
    }
    if (minutes && !sms) {
        return `${minutes} MIN`;
    }

    if (!minutes && !sms) {
        return '';
    }

    return `${minutes} MIN ir ${sms} SMS`;
};
export const getTotalPriceBlockAlignment = (isStickyOnMobile: boolean) =>
    !isResMoreThanExtraSmall() && isStickyOnMobile ? 'horizontal' : 'vertical';
export const getTotalPriceBlockSize = (isStickyOnMobile: boolean) => {
    if (!isResMoreThanExtraSmall() && isStickyOnMobile) {
        return 'extraSmall';
    }
    if (isMobileRes() && isStickyOnMobile) {
        return 'small';
    }

    return 'normal';
};
export const getPreorderText = ({ date, t, cart }: { date: string; t: { [key: string]: string }; cart: Cart }) => {
    const segment = getHybrisSegment();
    const localizedDateString = new Date(date).toLocaleDateString('lt-LT', { month: 'long', day: 'numeric' });
    const isWithEsim = selectIsWithEsim(cart);
    const orderNumber = cart.code;

    if (segment === 'b2c') {
        return withParams(t[`eshop.preorder.b2c.success${isWithEsim ? '.esim' : ''}`], {
            date: localizedDateString,
            orderNumber,
            email: cart.deliveryAddress.email,
        });
    }

    if (segment === 'b2b') {
        if (cart.checkoutPaymentType === CheckoutPaymentMethods.Bank) {
            return withParams(t['eshop.preorder.b2b.bank.success'], { date: localizedDateString });
        }

        return withParams(t['eshop.preorder.b2b.not_bank.success'], { date: localizedDateString });
    }

    throw new Error('Error : Illegal segmentType found when computing preorder success message.');
};

/**
 * Converts code with format MONTHS_12 leaving just number 12
 */
export const getPeriodFromCode = (code: string) => {
    const [, period] = code.split('_');

    return period;
};

export const getCheckoutType = (cart: Cart): string => {
    if (selectHasDevice(cart) && selectHasRatePlan(cart)) {
        return 'Equipment + Service checkout';
    }

    if (selectHasDevice(cart)) {
        return 'Equipment checkout';
    }

    if (selectHasRatePlan(cart)) {
        return 'Service checkout';
    }

    return 'Unknown Checkout';
};
